import PropTypes from "prop-types";

import useReadAloudMode from "@/view/ReadAloudControls/useReadAloudMode";
import useActivityPanel from "@/view/utils/useActivityPanel";
import usePresentModeContext from "@/view/utils/usePresentModeContext";
import useUserContentUIContext from "@/view/utils/useUserContentUIContext";
import {
  alertCss,
  containerCss,
  headerCss,
  innerMainSectionCss,
  innerPollSectionCss,
  mainSectionCss,
  pageBottomCss,
  panelCss,
  panelWrapperCss,
  stickyBarContainerCss,
  unauthContainerCss,
  videoPresentModeContainerCss,
  videoPresentModeInnerMainSectionCss,
  videoPresentModeMainSectionCss,
  videoPresentModePanelCss,
} from "./ContentLayout.style";

const propTypes = {
  children: PropTypes.node,
  isVideoPresentModeWithoutQuiz: PropTypes.bool,
};

const unauthPropTypes = {
  children: PropTypes.node,
};

const unauthMainSectionPropTypes = {
  children: PropTypes.node,
  hasHeaderImage: PropTypes.bool,
};

const headerPropTypes = {
  ...propTypes,
};

const stickyBarContainerPropTypes = {
  ...propTypes,
  overrideToTop: PropTypes.bool,
};

const mainSectionPropTypes = {
  ...propTypes,
  contentIsVideo: PropTypes.bool,
  hasHeaderImage: PropTypes.bool,
};

const panelPropTypes = {
  ...propTypes,
  contentIsVideo: PropTypes.bool,
  hasHeaderImage: PropTypes.bool,
};

const ContentLayout = ({ children, inline = true }) => {
  const { isVideoPresentModeWithoutQuiz } = usePresentModeContext();
  const containerStyles = isVideoPresentModeWithoutQuiz
    ? [containerCss(inline), videoPresentModeContainerCss]
    : containerCss(inline);

  return <div css={containerStyles}>{children}</div>;
};

const UnauthLayout = ({ children }) => {
  return <div css={unauthContainerCss}>{children}</div>;
};

const UnauthMainSection = ({ children, hasHeaderImage }) => {
  const { uiType } = useUserContentUIContext();
  const { isActivityPanelExpanded } = useActivityPanel();

  const mainSectionStyles = mainSectionCss({
    hasHeaderImage,
    contentIsVideo: false,
    isActivityPanelExpanded,
    uiType,
  });
  const innerMainSectionStyles = innerMainSectionCss({
    isActivityPanelExpanded,
    isVideoPresentModeActive: false,
  });

  return (
    <article css={mainSectionStyles}>
      <div css={innerMainSectionStyles}>{children}</div>
    </article>
  );
};

const Header = ({ children }) => {
  const { uiType } = useUserContentUIContext();

  return <div css={headerCss(uiType)}>{children}</div>;
};

const StickyBarContainer = ({ children, overrideToTop = false }) => {
  const { isReadAloudModeActive: readAloud } = useReadAloudMode();
  return (
    <div css={stickyBarContainerCss(overrideToTop, readAloud)}>{children}</div>
  );
};

const Alert = ({ children }) => <div css={alertCss}>{children}</div>;

const PollSection = ({ children }) => {
  const { uiType } = useUserContentUIContext();

  return <div css={innerPollSectionCss(uiType)}>{children}</div>;
};

const MainSection = ({ children, contentIsVideo, hasHeaderImage }) => {
  const { isVideoPresentModeActive, isVideoPresentModeWithoutQuiz } =
    usePresentModeContext();
  const { uiType } = useUserContentUIContext();
  const { isActivityPanelExpanded } = useActivityPanel();

  const defaultMainSectionStyles = mainSectionCss({
    hasHeaderImage,
    contentIsVideo,
    isActivityPanelExpanded,
    uiType,
  });
  const mainSectionStyles = isVideoPresentModeActive
    ? [defaultMainSectionStyles, videoPresentModeMainSectionCss]
    : defaultMainSectionStyles;

  const defaultInnerMainSectionStyles = innerMainSectionCss({
    isActivityPanelExpanded,
    isVideoPresentModeActive,
  });
  const innerMainSectionStyles = isVideoPresentModeActive
    ? [
        defaultInnerMainSectionStyles,
        videoPresentModeInnerMainSectionCss(isVideoPresentModeWithoutQuiz),
      ]
    : defaultInnerMainSectionStyles;

  return (
    <article css={mainSectionStyles}>
      <div css={innerMainSectionStyles}>{children}</div>
    </article>
  );
};

const Panel = ({ children, contentIsVideo, hasHeaderImage }) => {
  const {
    isPresentModeActive,
    isVideoPresentModeActive,
    isVideoPresentModeWithoutQuiz,
  } = usePresentModeContext();
  const { isActivityPanelExpanded } = useActivityPanel();
  const defaultPanelStyles = panelCss(
    contentIsVideo,
    hasHeaderImage,
    isActivityPanelExpanded,
    isPresentModeActive,
  );
  const panelStyles = isVideoPresentModeActive
    ? [
        defaultPanelStyles,
        videoPresentModePanelCss(isVideoPresentModeWithoutQuiz),
      ]
    : defaultPanelStyles;

  return (
    <div css={panelWrapperCss}>
      <div css={panelStyles}>{children}</div>
    </div>
  );
};

const PageBottom = ({ children }) => <div css={pageBottomCss}>{children}</div>;

ContentLayout.propTypes = propTypes;
UnauthLayout.propTypes = unauthPropTypes;
UnauthMainSection.propTypes = unauthMainSectionPropTypes;
Header.propTypes = headerPropTypes;
StickyBarContainer.propTypes = stickyBarContainerPropTypes;
Alert.propTypes = propTypes;
PollSection.propTypes = propTypes;
MainSection.propTypes = mainSectionPropTypes;
Panel.propTypes = panelPropTypes;
PageBottom.propTypes = propTypes;

ContentLayout.UnauthLayout = UnauthLayout;
ContentLayout.UnauthMainSection = UnauthMainSection;
ContentLayout.Header = Header;
ContentLayout.StickyBarContainer = StickyBarContainer;
ContentLayout.Alert = Alert;
ContentLayout.PollSection = PollSection;
ContentLayout.MainSection = MainSection;
ContentLayout.Panel = Panel;
ContentLayout.PageBottom = PageBottom;

export default ContentLayout;
